import {store} from '../store';
import {formatCurrencyString} from '../util/currency';
import {DefaultCart, cartSummaryViewData} from '../reducers/cartReducer';
import {updateMerchantTaxConfig} from '../reducers/merchantConfigurationReducer';
import {type ICartMetaData} from '../../../@type/woocommerce/cart-calculation';
import {buildSubscriptionFirstRenewalString, buildSubscriptionPriceMetaData} from '../util/subscription';
import {$qs, $qsAll} from '../../../@shared/ts/dom';
import {getLocaleText} from '../util/translation';
import {type CheckoutData} from '../models/CheckoutData';

export function initSummary(checkoutData: CheckoutData) {
	initSummaryEvents();

	// Setup tax
	store.dispatch(updateMerchantTaxConfig({
		displayPricesInCartAndCheckout: (checkoutData.wc_tax_price_display === 'incl') ? 'includeTax' : 'excludeTax',
	}));
}

function initSummaryEvents() {
	// OrderSummaryDropdown has been combined with the code in slideUpView.ts because it is close in format to the existing slide-up views.

	// Keep summary related rendering up to date.
	store.subscribe(() => {
		renderSummaries();
		renderCartTotals();
	});
}

/**
 * Rerenders the subtotal -> total list.
 */
function renderSummaries() {
	clearRenderedSummaries();

	let cartSummariesHTML = '';

	for (const cartKey of Object.keys(store.getState().calculatedCarts)) {
		let summaryHTML = '';
		const {cartSummary, cartMeta} = cartSummaryViewData(cartKey)();

		const summaryTitleHTML = cartKey === '0' ? '' : `
<li class="summary-title">
	<div>${getLocaleText('Recurring totals')}</div>
	<div></div>
</li>`;

		for (const line of cartSummary) {
			// Insert border before total
			summaryHTML += line === cartSummary[cartSummary.length - 1] ? '<hr>' : '';
			summaryHTML += renderSummaryLine(line.key, line.value, cartMeta);
		}

		cartSummariesHTML += `
<div class="cart-summary" data-cart-key="${cartKey}">
	<ul class="cart-summary-list">
		<hr>
		${summaryTitleHTML}
		${summaryHTML}
	</ul>
	<p class="first-renewal muted">${buildSubscriptionFirstRenewalString(cartMeta)}</p>
</div>`;
	}

	// New
	$qs('#pp-summary-lines-body')?.insertAdjacentHTML('beforeend', cartSummariesHTML);
	// Mobile
	$qs('#pp-summary-lines-body-mobile')?.insertAdjacentHTML('beforeend', cartSummariesHTML);
}

/**
 * Clears all existing summary lines from the html dom before rerendering the summary view.
 */
function clearRenderedSummaries() {
	for (const $summary of $qsAll('.cart-summary')) {
		$summary.remove();
	}
}

/**
 * Renders a single summary line
 * @param name The name of the summary line.
 * @param amount The value of the summary line.
 * @param options Optional css class customization object.
 */
function renderSummaryLine(name: string, amount: number, cartMeta: ICartMetaData): string {
	let priceMetaHTML = '';
	if (cartMeta.subscription) {
		priceMetaHTML = `<span class="muted">${buildSubscriptionPriceMetaData(cartMeta)}</span>`;
	}

	const summaryLineType = name.includes('pp-coupon-remove-button') ? ' data-summary-line-type="coupon"' : '';

	return `
<li class="summary-line" data-raw-cost="${amount}"${summaryLineType}>
	<div>${name}</div>
	<div class="pp-recalculate-blur" >${formatCurrencyString(amount)}${priceMetaHTML}</div>
</li>`;
}

/**
 * Renders the total on the pay button and some other areas.
 */
function renderCartTotals(): void {
	// Clear totals.
	$qsAll('.pp-summary-total', $element => {
		$element.innerHTML = '';
	});

	$qsAll('.pp-summary-total', $element => {
		$element.innerHTML += `<span>${formatCurrencyString(DefaultCart.total())}</span>`;
	});

	if (Object.keys(store.getState().calculatedCarts).length > 1) {
		$qsAll('.pp-summary-total', $element => {
			$element.innerHTML += `<span class="flex pp-gap-2"><span class="muted"> + </span><span class="muted">${getLocaleText('Recurring')}</span></span>`;
		});
	}
}
